import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of, timer, forkJoin, throwError } from 'rxjs';
import { catchError, switchMap, retry, withLatestFrom, mergeMap } from 'rxjs/operators';

import * as PolicyActions from './policy.actions';
import { HttpErrorResponse } from '@angular/common/http';
import { globalMessages } from '../../shared/global-message';
import { PortletService } from '../../features/billing/services/portlet.service';
import { StepUpAuthService } from '../../shared/services/step-up-auth.service';

@Injectable()
export class PolicyEffects {
  private actions$ = inject(Actions);
  private portletService = inject(PortletService);
  private store = inject(Store);
  private stepUpAuth = inject(StepUpAuthService);

  // Helper methods for HTTP calls with retry logic
  private fetchDCPolicy(policyNumber: string) {
    const request = { policyNumber };
    return this.portletService.orchestrateData(globalMessages.getPolicyData, request).pipe(
      retry({
        count: 2,
        delay: (error) =>
          error instanceof HttpErrorResponse && error.status >= 502
            ? timer(2000)
            : throwError(() => error),
      }),
      catchError((error) => of({ _error: true, message: error.message }))
    );
  }

  private fetchDCBilling(policyNumber: string) {
    const request = { policyNumber };
    return this.portletService.orchestrateData(globalMessages.getBillingData, request).pipe(
      retry({
        count: 2,
        delay: (error) =>
          error instanceof HttpErrorResponse && error.status >= 502
            ? timer(2000)
            : throwError(() => error),
      }),
      catchError((error) => of({ _error: true, message: error.message }))
    );
  }

  private fetchDMS(policyNumber: string) {
    const request = { policyNumber };
    return this.portletService.orchestrateData(globalMessages.getReceipts, request).pipe(
      retry({
        count: 2,
        delay: (error) =>
          error instanceof HttpErrorResponse && error.status >= 502
            ? timer(2000)
            : throwError(() => error),
      }),
      catchError((error) => of({ _error: true, message: error.message }))
    );
  }

  private fetchPaymentSchedule(policyNumber: string, policySnapshot: any) {
    const effectiveDate = policySnapshot?.policyInfo?.effectiveDate || '';
    const expirationDate = policySnapshot?.policyInfo?.expirationDate || '';

    const request = {
      policyNumber,
      effectiveDate,
      expirationDate,
    };

    return this.portletService.orchestrateData(globalMessages.getBillingPayPlan, request).pipe(
      retry({
        count: 2,
        delay: (error) =>
          error instanceof HttpErrorResponse && error.status >= 502
            ? timer(2000)
            : throwError(() => error),
      }),
      catchError((error) => of({ _error: true, message: error.message }))
    );
  }

  loadPolicyData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PolicyActions.loadPolicyData),
      withLatestFrom(this.store.select((state: any) => state.policy?.data)),
      switchMap(([{ policyNumber }, storeData]) => {
        const existing = storeData?.[policyNumber];
        const hasPolicySnapshot = !!existing?.policySnapshot;
        const hasBillingSnapshot = !!existing?.billingSnapshot;
        const hasDmsSnapshot = !!existing?.dmsSnapshot;

        // If all data already exists, immediately dispatch success actions to reset loading flags
        if (hasPolicySnapshot && hasBillingSnapshot && hasDmsSnapshot) {
          return [
            PolicyActions.loadDCPolicySuccess({
              policyNumber,
              policySnapshot: existing.policySnapshot,
            }),
            PolicyActions.loadDCBillingSuccess({
              policyNumber,
              billingSnapshot: existing.billingSnapshot,
            }),
            PolicyActions.loadDMSSuccess({ policyNumber, dmsSnapshot: existing.dmsSnapshot }),
          ];
        }

        // Build array of observables only for missing data
        const calls: any[] = [];
        const callLabels: string[] = [];

        if (!hasPolicySnapshot) {
          calls.push(this.fetchDCPolicy(policyNumber));
          callLabels.push('policy');
        }
        if (!hasBillingSnapshot) {
          calls.push(this.fetchDCBilling(policyNumber));
          callLabels.push('billing');
        }
        if (!hasDmsSnapshot) {
          calls.push(this.fetchDMS(policyNumber));
          callLabels.push('dms');
        }

        return forkJoin(calls).pipe(
          switchMap((results: any[]) => {
            const actions: any[] = [];
            let resultIndex = 0;

            // For skipped calls, dispatch success with existing data to reset loading flags
            if (hasPolicySnapshot) {
              actions.push(
                PolicyActions.loadDCPolicySuccess({
                  policyNumber,
                  policySnapshot: existing.policySnapshot,
                })
              );
            }
            if (hasBillingSnapshot) {
              actions.push(
                PolicyActions.loadDCBillingSuccess({
                  policyNumber,
                  billingSnapshot: existing.billingSnapshot,
                })
              );
            }
            if (hasDmsSnapshot) {
              actions.push(
                PolicyActions.loadDMSSuccess({ policyNumber, dmsSnapshot: existing.dmsSnapshot })
              );
            }

            // For fetched calls, dispatch based on response
            if (callLabels.includes('policy')) {
              const dcPolicyData = results[resultIndex++];
              actions.push(
                dcPolicyData?._error
                  ? PolicyActions.loadDCPolicyFailure({
                      policyNumber,
                      error: dcPolicyData.message,
                    })
                  : PolicyActions.loadDCPolicySuccess({
                      policyNumber,
                      policySnapshot: dcPolicyData,
                    })
              );
            }

            if (callLabels.includes('billing')) {
              const dcBillingData = results[resultIndex++];
              actions.push(
                dcBillingData?._error
                  ? PolicyActions.loadDCBillingFailure({ error: dcBillingData.message })
                  : PolicyActions.loadDCBillingSuccess({
                      policyNumber,
                      billingSnapshot: dcBillingData,
                    })
              );
            }

            if (callLabels.includes('dms')) {
              const dmsData = results[resultIndex++];
              actions.push(
                dmsData?._error
                  ? PolicyActions.loadDMSFailure({ error: dmsData.message })
                  : PolicyActions.loadDMSSuccess({
                      policyNumber,
                      dmsSnapshot: dmsData,
                    })
              );
            }

            return actions;
          })
        );
      })
    )
  );

  loadDCPolicy$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PolicyActions.loadDCPolicy),
      withLatestFrom(this.store.select((state: any) => state.policy?.data)),
      switchMap(([{ policyNumber }, storeData]) => {
        const existing = storeData?.[policyNumber];
        if (existing?.policySnapshot) {
          // Already have policy snapshot, update stepUpAuth and dispatch success
          const zipcode = existing.policySnapshot?.propertyAddressInfo?.zipCode || '';
          this.stepUpAuth.setPolicyNumber(policyNumber);
          this.stepUpAuth.setZipcode(zipcode);
          return [
            PolicyActions.loadDCPolicySuccess({
              policyNumber,
              policySnapshot: existing.policySnapshot,
            }),
          ];
        }

        return this.fetchDCPolicy(policyNumber).pipe(
          switchMap((dcPolicyData: any) => {
            if (dcPolicyData?._error) {
              return [
                PolicyActions.loadDCPolicyFailure({ policyNumber, error: dcPolicyData.message }),
              ];
            }
            const zipcode = dcPolicyData?.propertyAddressInfo?.zipCode || '';
            this.stepUpAuth.setPolicyNumber(policyNumber);
            this.stepUpAuth.setZipcode(zipcode);
            return [
              PolicyActions.loadDCPolicySuccess({
                policyNumber,
                policySnapshot: dcPolicyData,
              }),
            ];
          })
        );
      })
    )
  );

  /**
   * Listens for loadDCPolicySuccess and automatically fetches the payment schedule.
   * This guarantees policySnapshot is already in the store when this effect runs.
   */
  getPaymentSchedule$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PolicyActions.loadDCPolicySuccess),
      switchMap(({ policyNumber, policySnapshot }) => {
        return this.fetchPaymentSchedule(policyNumber, policySnapshot).pipe(
          switchMap((response: any) => {
            if (response?._error) {
              return [PolicyActions.loadPaymentScheduleFailure({ error: response.message })];
            }
            return [
              PolicyActions.loadPaymentScheduleSuccess({
                policyNumber,
                paymentSchedule: response,
              }),
            ];
          })
        );
      })
    )
  );
}
